import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { adsApi } from '@/api/client';
import {
  buildKeywordNegativeApplyAction,
  buildKeywordPauseAction,
  getKeywordNegativeAlternate,
} from '@/api/keywordRecommendationApply';
import type { KeywordRecommendationItem, SpKeywordApplyAction } from '@/types/keywordRecommendations';

type PlacementMode = 'same' | 'other';

type CampaignOption = {
  id: string;
  name: string;
  targetingType?: string;
};

type AdGroupOption = { id: string; name: string };

function parseSpCampaignOptions(data: unknown): CampaignOption[] {
  if (!data || typeof data !== 'object') return [];
  const raw = data as Record<string, unknown>;
  const items = Array.isArray(raw.campaigns)
    ? raw.campaigns
    : Array.isArray(data)
      ? (data as unknown[])
      : [];
  const options: CampaignOption[] = [];
  for (const item of items) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const adProduct = String(row.adProduct ?? row.ad_product ?? '').toUpperCase();
    if (adProduct && adProduct !== 'SP') continue;
    const id = String(row.campaignId ?? row.campaign_id ?? '').trim();
    if (!id) continue;
    const name = String(row.name ?? row.campaignName ?? row.campaign_name ?? id);
    const targetingTypeRaw = String(row.targetingType ?? row.targeting_type ?? '').toUpperCase();
    options.push({
      id,
      name,
      ...(targetingTypeRaw ? { targetingType: targetingTypeRaw } : {}),
    });
  }
  return options.sort((a, b) => a.name.localeCompare(b.name));
}

function parseAdGroupsPayload(data: unknown): AdGroupOption[] {
  if (!data || typeof data !== 'object') return [];
  const raw = data as Record<string, unknown>;
  const items = Array.isArray(raw.ad_groups) ? raw.ad_groups : [];
  return items
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const row = item as Record<string, unknown>;
      const id = row.ad_group_id != null ? String(row.ad_group_id) : '';
      if (!id) return null;
      const name = row.name != null ? String(row.name) : id;
      return { id, name };
    })
    .filter((x): x is AdGroupOption => x !== null);
}

export function KeywordNegativeApplyModal({
  row,
  profileId,
  pending,
  onConfirm,
  onCancel,
}: {
  row: KeywordRecommendationItem;
  profileId: string;
  pending: boolean;
  onConfirm: (actions: SpKeywordApplyAction[]) => void;
  onCancel: () => void;
}) {
  const alternate = getKeywordNegativeAlternate(row);
  const [placement, setPlacement] = useState<PlacementMode>('same');
  const [campaignId, setCampaignId] = useState('');
  const [adGroupId, setAdGroupId] = useState('');
  const [alsoPause, setAlsoPause] = useState(false);

  const { data: campaignsData, isLoading: campaignsLoading } = useQuery({
    queryKey: ['keyword-negative-picker-campaigns', profileId],
    queryFn: async () => {
      const res = await adsApi.getProfileCampaigns(profileId, null, { adProduct: 'SP' });
      if (!res.success) throw new Error('Failed to load campaigns');
      return res.data;
    },
    enabled: !!profileId && placement === 'other',
  });

  const campaignOptions = useMemo(() => parseSpCampaignOptions(campaignsData), [campaignsData]);

  const { data: adGroupsData, isLoading: adGroupsLoading } = useQuery({
    queryKey: ['keyword-negative-picker-ad-groups', profileId, campaignId],
    queryFn: async () => {
      const res = await adsApi.listSpAdGroups(profileId, {
        campaign_ids: [campaignId],
        states: ['ENABLED', 'PAUSED', 'ARCHIVED'],
        max_results: 500,
      });
      if (!res.success) throw new Error('Failed to load ad groups');
      return res.data;
    },
    enabled: !!profileId && placement === 'other' && !!campaignId,
  });

  const adGroupOptions = useMemo(() => parseAdGroupsPayload(adGroupsData), [adGroupsData]);

  useEffect(() => {
    if (placement !== 'other') return;
    if (!campaignId && campaignOptions.length > 0) {
      setCampaignId(campaignOptions[0].id);
    }
  }, [placement, campaignId, campaignOptions]);

  useEffect(() => {
    if (placement !== 'other') return;
    setAdGroupId('');
  }, [placement, campaignId]);

  useEffect(() => {
    if (placement !== 'other' || adGroupOptions.length !== 1) return;
    setAdGroupId(adGroupOptions[0].id);
  }, [placement, adGroupOptions]);

  if (!alternate) return null;

  const placementLabel =
    placement === 'same'
      ? `${alternate.campaignName || alternate.campaignId} (same ad group)`
      : campaignOptions.find((c) => c.id === campaignId)?.name || campaignId;

  const canConfirm =
    placement === 'same' ||
    (!!campaignId && !!adGroupId && !adGroupsLoading);

  const handleConfirm = () => {
    const override =
      placement === 'other' ? { campaignId, adGroupId } : undefined;
    const actions: SpKeywordApplyAction[] = [
      buildKeywordNegativeApplyAction(alternate, override),
    ];
    if (alsoPause) {
      actions.push(buildKeywordPauseAction(row));
    }
    onConfirm(actions);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-surface rounded-xl shadow-xl max-w-lg w-full p-5 space-y-4">
        <h3 className="text-base font-semibold text-foreground">Add negative keyword</h3>

        <div className="text-sm text-foreground-muted space-y-1">
          <p>
            <span className="text-foreground-subtle">Keyword:</span>{' '}
            <strong>{alternate.keywordText}</strong>
          </p>
          <p>
            <span className="text-foreground-subtle">Match type:</span>{' '}
            <strong>{alternate.matchType.replace('NEGATIVE_', '')}</strong>
          </p>
        </div>

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium text-foreground">Placement</legend>
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input
              type="radio"
              name="negative-placement"
              checked={placement === 'same'}
              onChange={() => setPlacement('same')}
              className="mt-0.5"
            />
            <span>
              Same ad group (recommended)
              <span className="block text-xs text-foreground-subtle">
                {alternate.campaignName || alternate.campaignId}
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input
              type="radio"
              name="negative-placement"
              checked={placement === 'other'}
              onChange={() => setPlacement('other')}
              className="mt-0.5"
            />
            <span>Another campaign</span>
          </label>
        </fieldset>

        {placement === 'other' && (
          <div className="space-y-3 pl-1">
            <div>
              <label className="block text-xs font-medium text-foreground-muted mb-1">Campaign</label>
              {campaignsLoading ? (
                <div className="flex items-center gap-2 text-sm text-foreground-subtle">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading campaigns…
                </div>
              ) : (
                <select
                  value={campaignId}
                  onChange={(e) => setCampaignId(e.target.value)}
                  className="w-full border border-border rounded-lg px-2 py-1.5 text-sm"
                >
                  {campaignOptions.length === 0 && (
                    <option value="">No SP campaigns found</option>
                  )}
                  {campaignOptions.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                      {c.targetingType === 'AUTO' ? ' (AUTO)' : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-muted mb-1">Ad group</label>
              {!campaignId ? (
                <p className="text-sm text-foreground-subtle">Select a campaign first.</p>
              ) : adGroupsLoading ? (
                <div className="flex items-center gap-2 text-sm text-foreground-subtle">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading ad groups…
                </div>
              ) : (
                <select
                  value={adGroupId}
                  onChange={(e) => setAdGroupId(e.target.value)}
                  className="w-full border border-border rounded-lg px-2 py-1.5 text-sm"
                >
                  <option value="">Select ad group</option>
                  {adGroupOptions.map((ag) => (
                    <option key={ag.id} value={ag.id}>
                      {ag.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
        )}

        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={alsoPause}
            onChange={(e) => setAlsoPause(e.target.checked)}
          />
          Also pause keyword
        </label>

        <p className="text-xs text-foreground-subtle">
          This creates an ad-group negative in Amazon Ads
          {placement === 'same' ? ` in ${placementLabel}` : placementLabel ? ` in ${placementLabel}` : ''}
          and does not pause the keyword unless you also apply Pause.
        </p>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={pending}
            className="px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-page disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={pending || !canConfirm}
            className="px-3 py-1.5 text-sm bg-brand text-white rounded-lg hover:opacity-90 disabled:opacity-50"
          >
            {pending ? 'Applying…' : 'Confirm add negative'}
          </button>
        </div>
      </div>
    </div>
  );
}
